import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Space, Spin, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getApi, postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import type { WizardState, WizardAction } from '../wizardReducer';
import {
	buildConfirmPayload,
	formatCurrency,
	getErrorLocus,
	getErrorStep,
	isPendingFieldKey,
	parseConfirmErrors,
	type PreValidationError,
	type SmartImportResponse,
} from './step4Confirm';
import styles from './Step4PreviewConfirm.module.scss';

interface Step4PreviewConfirmProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	payPeriodId: string;
	payPeriodLabel: string; // "MM/DD/YYYY - MM/DD/YYYY"; may be empty
	companyId: string;
	onBack: () => void;
	onCancel: () => void;
	// ENG-374: on a successful import that created new fields, the labels of those
	// fields are passed up so the PARENT can surface the "map GL accounts" reminder
	// modal after the wizard closes. Omitted/empty when nothing new was created.
	onImported: (newFieldLabels?: string[]) => void;
}

const FIELDS_URL = '/employee-cost/import/fields';
const CONFIRM_URL = '/employee-cost/import/smart';
// Silent "last used" mapping per company (SmartImportDefaultMapping on the
// backend) — POSTed here, after a successful Confirm Import, so only a
// mapping that was actually used for a real import becomes the new default.
// See handleNext in Step2MapColumns.tsx for where state.mappingsPayloadForSave
// gets captured, and applyDefaultMappingIfGoodMatch for where it's read back.
const DEFAULT_MAPPING_URL = '/employee-cost/import/default-mapping';
// See the matching comment on Step3MapEmployees.tsx's TABLE_SCROLL_Y — sizing
// this off the Modal body's own 85vh cap (SmartImportWizard.tsx) keeps the
// table itself as the only visible scroll surface in the normal case,
// instead of nesting its fixed-height internal scrollbar inside the modal
// body's own fallback scrollbar. This step can also show several conditional
// alert banners above the table (total mismatch, cleared-field warnings,
// confirm errors) — the offset assumes roughly one is visible at a time; if
// several stack at once the modal body's own fallback scroll still covers it.
const TABLE_SCROLL_Y = 'max(240px, calc(85vh - 380px))';

type AggregatedRow = WizardState['aggregatedRows'][number];

// Backend GET /import/fields response field shape (subset we need).
interface FetchedField {
	id: string;
	jsonId: string;
	name: string;
	isTotal: boolean;
}
interface FetchedSection {
	id: string;
	fields: FetchedField[];
}

/**
 * Step 4 — Preview & Confirm (WS-11). Read-only over wizard state: shows the import
 * header, a totals validation card, a 20-row preview grid, and the Confirm Import
 * button wired to POST /import/smart. Surfaces 422 pre-validation errors with
 * "Fix in Step 2/3" navigation, and non-422 failures with a retry CTA.
 */
export default function Step4PreviewConfirm({
	state,
	dispatch,
	payPeriodId,
	payPeriodLabel,
	companyId,
	onBack,
	onCancel,
	onImported,
}: Step4PreviewConfirmProps) {
	const [fieldNames, setFieldNames] = useState<Record<string, string>>({});
	const [fieldsLoading, setFieldsLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [confirmErrors, setConfirmErrors] = useState<PreValidationError[]>([]);
	const [genericError, setGenericError] = useState(false);

	// Resolve existing-field display names for the preview column headers (wizard
	// state carries only field ids/tempIds, not labels). Same call Step 2 makes.
	useEffect(() => {
		if (!companyId || !payPeriodId) {
			setFieldsLoading(false);
			return;
		}
		let cancelled = false;
		const load = async () => {
			setFieldsLoading(true);
			try {
				const res = await getApi(FIELDS_URL, { companyId, payPeriodId });
				if (cancelled) return;
				const sections: FetchedSection[] = res.data?.sections ?? [];
				const map: Record<string, string> = {};
				for (const s of sections) {
					for (const f of s.fields) {
						if (!f.isTotal) map[f.id] = f.name;
					}
				}
				setFieldNames(map);
			} catch {
				// Non-fatal: headers fall back to the field key / pending label.
			} finally {
				if (!cancelled) setFieldsLoading(false);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
	}, [companyId, payPeriodId]);

	// Ordered, de-duplicated list of target field keys (multiple source columns can
	// map to one field). Column order follows the Step 2 mapping order.
	const fieldKeys = useMemo(() => {
		const seen = new Set<string>();
		const ordered: string[] = [];
		for (const m of state.columnMappings) {
			if (
				m.category === 'employee_name' ||
				m.category === 'skip' ||
				m.fieldRef.type === 'none'
			) {
				continue;
			}
			const key =
				m.fieldRef.type === 'existing' ? m.fieldRef.fieldId : m.fieldRef.tempId;
			if (!seen.has(key)) {
				seen.add(key);
				ordered.push(key);
			}
		}
		// Defensive: include any value key not represented in columnMappings.
		for (const r of state.aggregatedRows) {
			for (const k of Object.keys(r.values)) {
				if (!seen.has(k)) {
					seen.add(k);
					ordered.push(k);
				}
			}
		}
		return ordered;
	}, [state.columnMappings, state.aggregatedRows]);

	const pendingLabelFor = (key: string): string | undefined =>
		state.pendingFields.find((pf) => pf.tempId === key)?.label;

	const headerLabel = (key: string): string =>
		isPendingFieldKey(key)
			? pendingLabelFor(key) ?? key
			: fieldNames[key] ?? key;

	const employeeByRowId = useMemo(() => {
		const m = new Map<string, WizardState['employeeRows'][number]>();
		for (const r of state.employeeRows) m.set(r.rowId, r);
		return m;
	}, [state.employeeRows]);

	// ENG-414: rows marked Skip in Step 3 are excluded from the import, so they must
	// not appear in the preview grid, the "Adding N rows" header, or the column
	// totals. The `skipped` flag lives on employeeRows, so the numeric twins in
	// aggregatedRows are filtered by joining on rowId — filtering is NOT automatic
	// here. This is the single source of truth for "what Step 4 shows and imports";
	// it mirrors the `importableRows` filter in buildConfirmPayload.
	const importableRows = useMemo(
		() =>
			state.aggregatedRows.filter(
				(row) => employeeByRowId.get(row.rowId)?.skipped !== true
			),
		[state.aggregatedRows, employeeByRowId]
	);

	// Per-cost-field totals across ALL importable rows (not just the 20 previewed)
	// for the pinned summary row.
	const columnTotals = useMemo(() => {
		const sums: Record<string, number> = {};
		for (const key of fieldKeys) {
			sums[key] = importableRows.reduce(
				(acc, row) => acc + (row.values[key] ?? 0),
				0
			);
		}
		return sums;
	}, [importableRows, fieldKeys]);

	// PDF-sourced imports carry the document's own printed grand total — a free
	// sanity check unique to invoices with a stated total line. Compares
	// against the sum of the pinned per-field totals row above, not
	// step4Confirm.ts's unused computeTotals().
	//
	// Prefer the employee table's own printed totals (printedDetailTotal) over
	// the invoice's "total due" (statedGrandTotal): the total due also carries
	// previous balances and payments that belong to no employee (e.g. BCBS
	// $46,186.54 due vs $14,717.23 of employee premiums), so comparing against
	// it flagged a mismatch on correctly imported invoices. `?? null` keeps an
	// older backend response (no printedDetailTotal) on the stated-total check.
	const statedGrandTotal = state.pdfExtraction?.statedGrandTotal ?? null;
	const printedDetailTotal = state.pdfExtraction?.printedDetailTotal ?? null;
	const expectedTotal = printedDetailTotal ?? statedGrandTotal;
	const importGrandTotal = useMemo(
		() => Object.values(columnTotals).reduce((sum, v) => sum + v, 0),
		[columnTotals]
	);
	const statedTotalMismatch =
		expectedTotal !== null && Math.abs(importGrandTotal - expectedTotal) > 0.01;
	// The invoice's total due differs from its employee lines (prior balance,
	// payments…) — worth one explanatory sentence so it isn't read as an error.
	const totalDueDiffers =
		printedDetailTotal !== null &&
		statedGrandTotal !== null &&
		Math.abs(statedGrandTotal - printedDetailTotal) > 0.01;

	const rowCount = importableRows.length;
	const fieldCount = fieldKeys.length;
	const anyNegated = state.columnMappings.some(
		(m) =>
			m.negate &&
			m.category !== 'skip' &&
			m.category !== 'employee_name' &&
			m.fieldRef.type !== 'none'
	);

	// Error-driven cell / row highlighting (422 path).
	const errorCells = useMemo(() => {
		const set = new Set<string>();
		for (const e of confirmErrors) {
			const key = e.fieldRef?.fieldId ?? e.fieldRef?.tempId;
			if (e.rowId && key) set.add(`${e.rowId}|${key}`);
		}
		return set;
	}, [confirmErrors]);
	const errorRows = useMemo(() => {
		const set = new Set<string>();
		for (const e of confirmErrors) {
			const key = e.fieldRef?.fieldId ?? e.fieldRef?.tempId;
			if (e.rowId && !key) set.add(e.rowId);
		}
		return set;
	}, [confirmErrors]);

	// Reconciliation aid (PDF-sourced imports only) — cells whose value differs
	// from what this employee had for the same field last period.
	const changedCells = useMemo(() => {
		const set = new Set<string>();
		if (!state.priorPeriodValues) return set;
		for (const row of importableRows) {
			for (const key of fieldKeys) {
				const entry = state.priorPeriodValues[`${row.rowId}|${key}`];
				if (entry && Math.abs((row.values[key] ?? 0) - entry.priorValue) > 0.01) {
					set.add(`${row.rowId}|${key}`);
				}
			}
		}
		return set;
	}, [state.priorPeriodValues, importableRows, fieldKeys]);

	// Fringe fields that had a nonzero value last period but no invoice
	// uploaded this cycle appears to cover — informational only (see
	// priorPeriodReconciliationService.ts). Excludes any field this import IS
	// actually updating (present in fieldKeys), since that field's own new
	// value already supersedes whatever the heuristic guessed.
	const clearedFieldWarnings = useMemo(() => {
		if (!state.priorPeriodValues) return [];
		const fieldKeySet = new Set(fieldKeys);
		const byField = new Map<string, { fieldName: string; count: number }>();
		for (const [mapKey, entry] of Object.entries(state.priorPeriodValues)) {
			if (!entry.shouldClear) continue;
			const fieldId = mapKey.split('|')[1];
			if (fieldKeySet.has(fieldId)) continue;
			const existing = byField.get(fieldId);
			byField.set(fieldId, { fieldName: entry.fieldName, count: (existing?.count ?? 0) + 1 });
		}
		return Array.from(byField.values());
	}, [state.priorPeriodValues, fieldKeys]);

	const columns: ColumnsType<AggregatedRow> = [
		{
			title: 'Employee',
			key: 'csvName',
			fixed: 'left',
			width: 220,
			render: (_: unknown, row) => {
				const twin = employeeByRowId.get(row.rowId);
				const isNew = twin?.matchStatus === 'new';
				// ENG-410: show the resolved CAP name, not the raw file name. New-band
				// rows are created under their (possibly edited) add-as-new name;
				// matched/saved rows show the mapped employee's fullName captured in
				// Step 3. Fall back to the file name so a cell is never blank.
				const displayName = isNew
					? twin?.addAsNewName ?? row.csvName
					: twin?.matchedEmployeeName ?? row.csvName;
				return (
					<span>
						<strong>{displayName}</strong>{' '}
						{isNew && <Tag color="green">new</Tag>}
					</span>
				);
			},
		},
		...fieldKeys.map((key) => ({
			title: (
				<span>
					{headerLabel(key)}{' '}
					{isPendingFieldKey(key) && <Tag color="green">new</Tag>}
				</span>
			),
			key,
			align: 'right' as const,
			onCell: (row: AggregatedRow) => {
				if (errorCells.has(`${row.rowId}|${key}`)) return { className: styles['step4__cell--error'] };
				if (changedCells.has(`${row.rowId}|${key}`)) return { className: styles['step4__cell--changed'] };
				return {};
			},
			render: (_: unknown, row: AggregatedRow) => {
				const amount = formatCurrency(row.values[key] ?? 0);
				const priorEntry = state.priorPeriodValues?.[`${row.rowId}|${key}`];
				if (!priorEntry || !changedCells.has(`${row.rowId}|${key}`)) return amount;
				return (
					<Tooltip title={`Last period: ${formatCurrency(priorEntry.priorValue)}`}>
						<span>{amount}</span>
					</Tooltip>
				);
			},
		})),
	];

	// Success toast: the count summary from the commit, plus an optional warnings
	// line (the backend currently always returns warnings: []).
	const emitSuccessToasts = (result: SmartImportResponse | null) => {
		if (!result) return;
		const parts = [
			result.createdEmployees > 0 && `${result.createdEmployees} employee(s) added`,
			result.reactivatedEmployees > 0 &&
				`${result.reactivatedEmployees} reactivated`,
			result.createdFields > 0 && `${result.createdFields} field(s) created`,
			`${result.upsertedCosts} cost value(s) imported`,
		]
			.filter(Boolean)
			.join(', ');
		toastText(`Import complete: ${parts}.`, 'success');
		if (result.warnings.length > 0) {
			toastText(
				`${result.warnings.length} warning(s): ${result.warnings
					.map((w) => w.message)
					.join('; ')}`,
				'info'
			);
		}
	};

	const handleConfirm = async () => {
		setSubmitting(true);
		setConfirmErrors([]);
		setGenericError(false);
		try {
			const payload = buildConfirmPayload(state, { companyId, payPeriodId });
			const res = await postApi(CONFIRM_URL, payload);
			const result = res.data as SmartImportResponse;
			emitSuccessToasts(result);
			// Silently remember this mapping as the company's new default for next
			// time — only reached once the import above has actually succeeded, so
			// an abandoned/cancelled session never overwrites a good default with a
			// half-finished one. Best-effort: never blocks or fails the import the
			// user is actually here for.
			if (state.mappingsPayloadForSave && state.mappingsPayloadForSave.length > 0) {
				void postApi(DEFAULT_MAPPING_URL, {
					companyId,
					mappings: state.mappingsPayloadForSave,
				}).catch(() => undefined);
			}
			// ENG-374: hand the new-field labels up to the parent BEFORE onImported()
			// closes the wizard (which dispatches RESET, clearing pendingFields). The
			// parent shows the "map GL accounts" reminder modal once the wizard is gone,
			// so the modal never stacks on top of the open wizard. No new fields → no
			// argument, same as before.
			const newFieldLabels =
				result.createdFields > 0
					? state.pendingFields.map((pf) => pf.label)
					: undefined;
			onImported(newFieldLabels);
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } })?.response?.status;
			if (status === 422) {
				setConfirmErrors(parseConfirmErrors(err));
			} else {
				setGenericError(true);
			}
			setSubmitting(false);
		}
	};

	const goToStep = (step: number) => {
		setConfirmErrors([]);
		setGenericError(false);
		dispatch({ type: 'GOTO_STEP', step });
	};

	return (
		<div className={styles['step4']}>
			{/* Preview header (§6.1) */}
			<div>
				<div className={styles['step4__header']}>
					Adding {rowCount} row{rowCount === 1 ? '' : 's'} into {fieldCount} cost
					field{fieldCount === 1 ? '' : 's'}.
					{anyNegated && ' (Some fields negated)'}
				</div>
			</div>

			{expectedTotal !== null && (
				<Alert
					className={styles['step4__pdf-total-check']}
					type={statedTotalMismatch ? 'warning' : 'success'}
					showIcon
					message={
						printedDetailTotal !== null
							? statedTotalMismatch
								? `The imported total (${formatCurrency(importGrandTotal)}) doesn't match the employee totals printed on the PDF (${formatCurrency(printedDetailTotal)}) — check that every amount column is mapped, and mapped only once, before confirming.`
								: `Imported total (${formatCurrency(importGrandTotal)}) matches the employee totals printed on the PDF (${formatCurrency(printedDetailTotal)}).`
							: statedTotalMismatch
							? `The imported total (${formatCurrency(importGrandTotal)}) doesn't match this document's stated total due (${formatCurrency(expectedTotal)}) — review before confirming.`
							: `Imported total (${formatCurrency(importGrandTotal)}) matches this document's stated total due (${formatCurrency(expectedTotal)}).`
					}
					description={
						totalDueDiffers
							? `The invoice's total due (${formatCurrency(statedGrandTotal as number)}) also includes amounts that don't belong to any employee, such as a previous balance or payments.`
							: undefined
					}
				/>
			)}

			{clearedFieldWarnings.length > 0 && (
				<Alert
					className={styles['step4__pdf-total-check']}
					type="warning"
					showIcon
					message="Some fringe fields may need manual review"
					description={
						<ul style={{ margin: 0, paddingLeft: 18 }}>
							{clearedFieldWarnings.map(({ fieldName, count }) => (
								<li key={fieldName}>
									{count} employee{count === 1 ? '' : 's'} had a value for <strong>{fieldName}</strong> last period, but no
									uploaded invoice this cycle appears to cover it — this import leaves it unchanged; review separately if
									coverage actually ended.
								</li>
							))}
						</ul>
					}
				/>
			)}

			{/* Data grid preview (§6.3) with a pinned per-field totals row */}
			<Spin spinning={fieldsLoading}>
				<Table<AggregatedRow>
					className={styles['step4__table']}
					columns={columns}
					dataSource={importableRows}
					rowKey="rowId"
					pagination={false}
					size="small"
					scroll={{ x: true, y: TABLE_SCROLL_Y }}
					rowClassName={(row) =>
						errorRows.has(row.rowId) ? styles['step4__row--error'] : ''
					}
					summary={() => (
						<Table.Summary fixed>
							<Table.Summary.Row className={styles['step4__summary-row']}>
								<Table.Summary.Cell index={0}>
									<strong>Total</strong>
								</Table.Summary.Cell>
								{fieldKeys.map((key, i) => (
									<Table.Summary.Cell key={key} index={i + 1} align="right">
										<strong>{formatCurrency(columnTotals[key] ?? 0)}</strong>
									</Table.Summary.Cell>
								))}
							</Table.Summary.Row>
						</Table.Summary>
					)}
				/>
			</Spin>

			{/* Pre-validation error panel (§6.4, 422) */}
			{confirmErrors.length > 0 && (
				<div className={styles['step4__errors']}>
					<div className={styles['step4__errors-heading']}>
						{confirmErrors.length} issue{confirmErrors.length === 1 ? '' : 's'}{' '}
						found — fix the highlighted items and re-import.
					</div>
					<ul className={styles['step4__errors-list']}>
						{confirmErrors.map((e, i) => {
							const locus = getErrorLocus(e, state.pendingFields, state.employeeRows);
							const step = getErrorStep(e);
							return (
								<li key={i}>
									{locus && <strong>{locus}: </strong>}
									{e.message}
									{step === 1 && (
										<Button type="link" size="small" onClick={() => goToStep(1)}>
											Fix in Step 2
										</Button>
									)}
									{step === 2 && (
										<Button type="link" size="small" onClick={() => goToStep(2)}>
											Fix in Step 3
										</Button>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{/* Generic commit-failure panel (§6.4, 409/500) */}
			{genericError && (
				<Alert
					type="error"
					showIcon
					message="Import failed. Please try again."
					action={
						<Button size="small" onClick={() => void handleConfirm()}>
							Retry
						</Button>
					}
				/>
			)}

			{/* Reminder line (§6.4) */}
			<div className={styles['step4__reminder']}>
				Values will be added to existing data for pay period{' '}
				{payPeriodLabel || 'the selected pay period'}.
			</div>

			{!payPeriodId && (
				<div className={styles['step4__no-period']}>
					Select a pay period before importing.
				</div>
			)}

			{/* Action footer */}
			<div className={styles['step4__footer']}>
				<Space>
					<Button className={styles['step4__btn-secondary']} onClick={onBack} disabled={submitting}>
						Previous
					</Button>
					<Button className={styles['step4__btn-secondary']} onClick={onCancel} disabled={submitting}>
						Cancel
					</Button>
					<Button
						type="primary"
						loading={submitting}
						disabled={submitting || !payPeriodId}
						onClick={() => void handleConfirm()}
						className="btn-black"
						style={{ height: '41px' }}
					>
						{submitting ? 'Importing…' : 'Confirm Import'}
					</Button>
				</Space>
			</div>
		</div>
	);
}
